import React, { useEffect, useState, useContext } from "react";
import QRCode from "qrcode.react";
import toastError from "../../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogContent, Paper, Typography, Button, DialogActions } from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  dialogPaper: {
    minWidth: 320,
    maxWidth: 400,
    width: "100%",
    padding: theme.spacing(2),
    margin: 0,
    [theme.breakpoints.down("xs")]: {
      minWidth: "90vw",
    },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(3),
  },
  qrCodeContainer: {
    padding: theme.spacing(3),
    backgroundColor: "white",
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  loadingText: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

const QrcodeModal = ({ open, onClose, whatsAppId, onOpenPairPhone, channel }) => {
  const classes = useStyles();
  const [qrCode, setQrCode] = useState("");
  const [whatsappData, setWhatsappData] = useState(null);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        // Usar rota admin para permitir Super Admin acessar conexões de outras empresas
        const route = user?.super
          ? `/whatsapp-admin/${whatsAppId}?session=0`
          : `/whatsapp/${whatsAppId}?session=0`;
        const { data } = await api.get(route);
        setQrCode(data.qrcode);
        setWhatsappData(data); // Salvar dados completos para verificar channel
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId, user?.super]);

  useEffect(() => {
    if (!whatsAppId || !socket || !user?.companyId) return undefined;
    const companyId = user.companyId;

    const onWhatsappData = (data) => {
      if (data.action === "update" && data.session.id === whatsAppId) {
        setQrCode(data.session.qrcode);
        // ✅ Atualizar whatsappData quando receber atualização do socket
        setWhatsappData(prev => prev ? { ...prev, ...data.session } : data.session);
      }

      if (data.action === "update" && data.session.qrcode === "") {
        onClose();
      }
    };
    socket.on(`company-${companyId}-whatsappSession`, onWhatsappData);

    return () => {
      socket.off(`company-${companyId}-whatsappSession`, onWhatsappData);
    };
  }, [whatsAppId, onClose, user?.companyId, socket]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="qr-code-dialog"
    >
      <DialogContent className={classes.content}>
        <Paper elevation={0} className={classes.dialogPaper}>
          <Typography variant="h6" color="primary" gutterBottom>
            {i18n.t("qrCode.message")}
          </Typography>
          <div className={classes.root}>
            {qrCode ? (
              <div className={classes.qrCodeContainer}>
                {/* Se o QR code começa com "2@", verificar se é texto (Baileys/WhatsMeow) ou base64 de imagem (Wuzapi) */}
                {qrCode.startsWith("2@") ? (
                  // Remover o prefixo "2@" para verificar o conteúdo
                  (() => {
                    const qrWithoutPrefix = qrCode.substring(2);
                    // Se após remover "2@" é base64 válido de imagem (começa com iVBORw0KGgo para PNG), é imagem
                    if (qrWithoutPrefix.match(/^[A-Za-z0-9+/=]+$/) && (qrWithoutPrefix.startsWith("iVBORw0KGgo") || qrWithoutPrefix.startsWith("/9j/"))) {
                      // É base64 de imagem (Wuzapi com prefixo 2@)
                      return (
                        <img 
                          src={`data:image/png;base64,${qrWithoutPrefix}`}
                          alt="QR Code"
                          style={{ maxWidth: "100%", height: "auto", display: "block", margin: "0 auto" }}
                        />
                      );
                    } else {
                      // É texto do protocolo WhatsApp Web (Baileys/WhatsMeow)
                      return (
                        <QRCode 
                          value={qrCode} 
                          size={280} 
                          style={{ maxWidth: "100%", height: "auto" }}
                        />
                      );
                    }
                  })()
                ) : qrCode.startsWith("data:") ? (
                  /* Se começa com data:, é base64 de imagem direto */
                  <img 
                    src={qrCode}
                    alt="QR Code"
                    style={{ maxWidth: "100%", height: "auto", display: "block", margin: "0 auto" }}
                  />
                ) : qrCode.match(/^[A-Za-z0-9+/=]+$/) && (qrCode.startsWith("iVBORw0KGgo") || qrCode.startsWith("/9j/")) ? (
                  /* Se é base64 puro de imagem (sem prefixo 2@) */
                  <img 
                    src={`data:image/png;base64,${qrCode}`}
                    alt="QR Code"
                    style={{ maxWidth: "100%", height: "auto", display: "block", margin: "0 auto" }}
                  />
                ) : (
                  /* Caso contrário, é texto do protocolo WhatsApp Web */
                  <QRCode 
                    value={qrCode} 
                    size={280} 
                    style={{ maxWidth: "100%", height: "auto" }}
                  />
                )}
              </div>
            ) : (
              <Typography variant="body1" className={classes.loadingText}>
                {i18n.t("qrCode.waiting")}
              </Typography>
            )}
          </div>
        </Paper>
        {/* ✅ Adicionar botão para abrir PairPhone se for WuzAPI - FORA do Paper para garantir visibilidade */}
        {(() => {
          const channelFromProp = channel;
          const channelFromData = whatsappData?.channel;
          const isWuzapi = 
            (channelFromProp && (channelFromProp.toLowerCase() === "wuzapi" || channelFromProp.toLowerCase() === "whatsmeow")) ||
            (channelFromData && (channelFromData.toLowerCase() === "wuzapi" || channelFromData.toLowerCase() === "whatsmeow"));
          
          console.log("[QRCODE MODAL DEBUG] Verificando botão:", {
            channelFromProp,
            channelFromData,
            isWuzapi,
            hasOnOpenPairPhone: !!onOpenPairPhone,
            whatsappData: whatsappData ? { id: whatsappData.id, channel: whatsappData.channel } : null
          });
          
          if (isWuzapi && onOpenPairPhone) {
            return (
              <div style={{ marginTop: 16, textAlign: "center", width: "100%", padding: "0 16px" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    onClose();
                    onOpenPairPhone();
                  }}
                  fullWidth
                >
                  Conectar via Telefone
                </Button>
              </div>
            );
          }
          
          return null;
        })()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
